/**
 * Lightweight Phase 1 i18n.
 *
 * Loads JSON catalogs at module init, resolves dotted keys against the active
 * locale, and falls back to English (then to the key itself). The selected
 * locale persists in localStorage under "odblox.locale".
 *
 * Subscribers are notified on locale change so React components re-render.
 */
import enCommon from "../locales/en/common.json";
import trCommon from "../locales/tr/common.json";
import enOperations from "../locales/en/operations.json";
import trOperations from "../locales/tr/operations.json";
import { env } from "./env";

export const SUPPORTED_LOCALES = ["en", "tr"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

type Catalog = Record<string, unknown>;

const STORAGE_KEY = "odblox.locale";

const catalogs: Record<Locale, Catalog> = {
  en: { ...enCommon, ...enOperations } as Catalog,
  tr: { ...trCommon, ...trOperations } as Catalog,
};

function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function readInitialLocale(): Locale {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isSupportedLocale(stored)) {
      return stored;
    }
  }
  return isSupportedLocale(env.DEFAULT_LOCALE) ? env.DEFAULT_LOCALE : "en";
}

let activeLocale: Locale = readInitialLocale();
const listeners = new Set<(locale: Locale) => void>();

export function getLocale(): Locale {
  return activeLocale;
}

export function setLocale(locale: Locale): void {
  activeLocale = locale;
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, locale);
  }
  for (const listener of listeners) {
    listener(locale);
  }
}

export function subscribeLocale(listener: (locale: Locale) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function lookup(catalog: Catalog, key: string): string | undefined {
  // Literal-first descent: at each level prefer a direct key match over
  // splitting on `.`. This lets catalogs carry flat keys whose names
  // contain dots (e.g. `audit.actions.user.create`, `scope.dateRange.all`)
  // without forcing every consumer to nest them — and without breaking
  // the conventional dotted-path lookup used everywhere else.
  const direct = (catalog as Record<string, unknown>)[key];
  if (typeof direct === "string") return direct;

  const idx = key.indexOf(".");
  if (idx < 0) return undefined;
  const head = key.slice(0, idx);
  const rest = key.slice(idx + 1);
  const next = (catalog as Record<string, unknown>)[head];
  if (next && typeof next === "object") {
    return lookup(next as Catalog, rest);
  }
  return undefined;
}

export function t(key: string, fallback?: string): string {
  const fromActive = lookup(catalogs[activeLocale], key);
  if (fromActive !== undefined) {
    return fromActive;
  }
  const fromEnglish = lookup(catalogs.en, key);
  if (fromEnglish !== undefined) {
    return fromEnglish;
  }
  return fallback ?? key;
}

export function formatMessage(
  key: string,
  vars: Record<string, string>,
  fallback?: string,
): string {
  let message = t(key, fallback);
  for (const [name, value] of Object.entries(vars)) {
    message = message.replaceAll(`{{${name}}}`, value);
    message = message.replaceAll(`{${name}}`, value);
  }
  return message;
}
